import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, Check, X, Star, Sparkles } from "lucide-react";

export interface KeywordSuggestion {
  keyword: string;
  intent: string;
  volume_estimate: string;
  difficulty: string;
  rationale: string;
}

const KeywordResearch = ({ onConfirm }: { onConfirm: (k: KeywordSuggestion) => void }) => {
  const [topic, setTopic] = useState("transferts privés, excursions, villes marocaines");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<KeywordSuggestion[]>([]);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-keyword-research", { body: { topic } });
      if (error) throw error;
      if ((data as any)?.fallback) {
        toast.warning((data as any)?.reason || "Service IA indisponible.");
        return;
      }
      const list = (data as any)?.keywords as KeywordSuggestion[] | undefined;
      if (!list?.length) throw new Error("Aucun mot-clé renvoyé");
      setItems(list);
      toast.success(`${list.length} mots-clés détectés`);
    } catch (e: any) {
      toast.error(e?.message || "Échec de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  const remove = (i: number) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const favorite = async (kw: KeywordSuggestion) => {
    const { error } = await (supabase as any).from("blog_keyword_favorites").insert({
      keyword: kw.keyword,
      intent: kw.intent || "",
      volume_estimate: kw.volume_estimate || "",
      difficulty: kw.difficulty || "",
      notes: kw.rationale || "",
    });
    if (error) toast.error(error.message);
    else toast.success("Ajouté aux favoris");
  };

  const confirm = async (kw: KeywordSuggestion, i: number) => {
    // 1) Push to shared queue (Guide dashboard table) — ignore duplicate
    const { error } = await (supabase as any)
      .from("blog_keyword_queue")
      .insert({ keyword: kw.keyword, status: "pending" });
    if (error && !/duplicate|unique/i.test(error.message)) {
      toast.error(error.message);
      return;
    }
    // 2) Notify Guide dashboard to refresh
    window.dispatchEvent(new CustomEvent("blog-keyword-queue-changed"));
    // 3) Bubble up + remove from local list (no duplicate)
    onConfirm(kw);
    remove(i);
    toast.success("Mot-clé envoyé au tableau du Guide");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Search className="h-5 w-5 text-primary" /> Recherche en temps réel — concurrents Maroctransfers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Sujet / niche à analyser…"
            maxLength={200}
          />
          <Button onClick={runAnalysis} disabled={loading} className="bg-gradient-gold text-primary-foreground whitespace-nowrap">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Lancer l'analyse
          </Button>
        </div>

        {items.length > 0 && (
          <div className="rounded-md border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mot-clé</TableHead>
                  <TableHead>Intention</TableHead>
                  <TableHead>Volume</TableHead>
                  <TableHead>Difficulté</TableHead>
                  <TableHead className="w-[220px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">
                      {k.keyword}
                      {k.rationale && <p className="text-xs text-muted-foreground mt-1">{k.rationale}</p>}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{k.intent}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{k.volume_estimate}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{k.difficulty}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="default" onClick={() => confirm(k, i)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <Check className="h-4 w-4 mr-1" />Confirmer
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(i)}>
                          <X className="h-4 w-4 mr-1" />Annuler
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => favorite(k)} title="Favoriser">
                          <Star className="h-4 w-4 text-amber-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KeywordResearch;